/**
 * Dev-only helper: seed fake notification items so every state + type can be
 * reviewed without waiting for real API activity. Registers two functions on
 * `window`:
 *
 *   __seedNotifications(n?)    -> pause poll, dispatch n fake items (default: all)
 *   __resumeNotifications()    -> clear seeded items and resume live polling
 *
 * Open the bell in the UI, then run these in the browser console.
 */
import { store } from '../../config/store';
import { notificationConstants } from '../../_constants/notification.constants';
import { setNotificationPollPaused } from './useNotificationPoll';

const MIN = 60 * 1000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

const buildItems = () => {
  const now = Date.now();
  return [
    {
      id: 'seed:booking_created:1',
      type: 'booking_created',
      title: 'New booking',
      subtitle: 'Jane Doe · Women\'s Cut · Thu 16 Apr',
      timestamp: now - 2 * MIN,
      icon: 'calendar',
      linkTo: { path: '/diary', state: {} },
      meta: { bookingId: 'seed-1' },
    },
    {
      id: 'seed:tip_received:1',
      type: 'tip_received',
      title: 'Tip received',
      subtitle: '£5.00 · Priya Patel',
      timestamp: now - 18 * MIN,
      icon: 'heart',
      linkTo: { path: '/transactions', state: {} },
      meta: { transactionId: 'seed-tx-1' },
    },
    {
      id: 'seed:payment_received:1',
      type: 'payment_received',
      title: 'Payment received',
      subtitle: '£65.00 · Priya Patel',
      timestamp: now - 19 * MIN,
      icon: 'card',
      linkTo: { path: '/transactions', state: {} },
      meta: { transactionId: 'seed-tx-2' },
    },
    {
      id: 'seed:booking_rescheduled:1',
      type: 'booking_rescheduled',
      title: 'Booking moved',
      subtitle: 'Alex Chen · Balayage · Sat 18 Apr',
      timestamp: now - 2 * HOUR,
      icon: 'swap',
      linkTo: { path: '/diary', state: {} },
      meta: { bookingId: 'seed-2' },
    },
    {
      id: 'seed:booking_cancelled:1',
      type: 'booking_cancelled',
      title: 'Booking cancelled',
      subtitle: 'Sam Okafor · Beard Trim · Fri 17 Apr',
      timestamp: now - 5 * HOUR,
      icon: 'x',
      linkTo: { path: '/diary', state: {} },
      meta: { bookingId: 'seed-3' },
    },
    {
      id: 'seed:low_stock:1',
      type: 'low_stock',
      title: 'Low stock',
      subtitle: 'Olaplex No.3 · 2 left',
      timestamp: now - 9 * HOUR,
      icon: 'box',
      linkTo: { path: '/product', state: {} },
      meta: { productId: 'seed-p-1' },
    },
    {
      id: 'seed:booking_created:2',
      type: 'booking_created',
      title: 'New booking',
      subtitle: 'Maria Rossi · Colour + Cut · Mon 21 Apr',
      timestamp: now - 1 * DAY - 3 * HOUR,
      icon: 'calendar',
      linkTo: { path: '/diary', state: {} },
      meta: { bookingId: 'seed-4' },
    },
    {
      id: 'seed:tip_received:2',
      type: 'tip_received',
      title: 'Tip received',
      subtitle: '£8.50 · Maria Rossi',
      timestamp: now - 1 * DAY - 4 * HOUR,
      icon: 'heart',
      linkTo: { path: '/transactions', state: {} },
      meta: { transactionId: 'seed-tx-3' },
    },
  ];
};

if (typeof window !== 'undefined') {
  window.__seedNotifications = (n) => {
    const all = buildItems();
    const items = typeof n === 'number' ? all.slice(0, n) : all;
    setNotificationPollPaused(true);
    store.dispatch({ type: notificationConstants.LOAD_SUCCESS, items });
    // Force all seeded items to appear unread by rolling lastViewedAt back.
    store.dispatch({ type: notificationConstants.HYDRATE_VIEWED_AT, timestamp: 0 });
    return items;
  };

  window.__resumeNotifications = () => {
    setNotificationPollPaused(false);
    store.dispatch({ type: notificationConstants.LOAD_SUCCESS, items: [] });
    return 'resumed';
  };

  // eslint-disable-next-line no-console
  console.info('[notifications] dev seed ready — __seedNotifications() / __resumeNotifications()');
}
